import { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Dropdown } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Loader from "../../../../Components/Loader";
import SearchInput from "../../../../Components/AdministrativeSettings/SearchInput";
import AdminStatusDropdown from "../../../../Components/AdministrativeSettings/AdminStatusDropdown";
import ClearFilterButton from "../../../../Components/ClearFilterButton";
import TablePagination from "../../../../Components/AdministrativeSettings/TablePagination";
import NoListDataAvailable from "../../../../Components/NoListDataAvailable";
import ConfirmationModal from "../../../../Components/ConfirmationModal";
import Toaster from "../../../../Components/Toaster";
import navdots from "../../../../Assets/images/navdots.svg";
import { RequestorService } from "../../../../Services/Requestor.Service";
import TokenService from "../../../../Services/Token.service";
import { Link, useNavigate } from "react-router-dom";

const pharmaStatuses = [
    { id: 1, label: "Active", value: 1 },
    { id: 0, label: "Inactive", value: 0 },
];

function AddPharma() {
    const [loading, setLoading] = useState(false);
    const [pharmaList, setPharmaList] = useState([]);
    const [totalPharma, setTotalPharma] = useState(0);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [searchPharmaName, setSearchPharmaName] = useState("");
    const [isSearchVisible, setSearchVisible] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagination, setPagination] = useState({ limit: 10, offset: 0, page: 1 });
    const [filterCount, setFilterCount] = useState(0);
    const [toastState, setToastState] = useState({ show: false, message: "", variant: "" });
    const [editingPharma, setEditingPharma] = useState(null);
    const [actionModal, setActionModal] = useState({ type: null, pharma: null });

    const totalPages = useMemo(() => {
        if (rowsPerPage === 0) {
            return 1;
        }
        const pages = Math.ceil(totalPharma / rowsPerPage);
        return pages > 0 ? pages : 1;
    }, [rowsPerPage, totalPharma]);

    const closeToast = () => setToastState({ show: false, message: "", variant: "" });

    const setToast = (message, variant) => {
        setToastState({ show: true, message, variant });
    };

    const fetchPharmaList = useCallback(() => {
        setLoading(true);
        const filters = {
            status: selectedStatuses,
            pharma_name: searchPharmaName?.trim() || "",
        };

        const payload = {
            limit: pagination.limit,
            offset: pagination.offset,
            filters: [filters],
        };

        RequestorService.getPharmaList(payload)
            .then((response) => {
                const data = response?.data || response || {};
                setPharmaList(data || []);
                setTotalPharma(response.total || 0);
            })
            .catch((error) => {
                console.error("Failed to fetch pharma list:", error);
                setPharmaList([]);
                setTotalPharma(0);
            })
            .finally(() => setLoading(false));
    }, [pagination, searchPharmaName, selectedStatuses]);

    useEffect(() => {
        fetchPharmaList();
    }, [fetchPharmaList]);

    useEffect(() => {
        const count = [
            selectedStatuses.length > 0 ? 1 : 0,
            searchPharmaName ? 1 : 0,
        ].reduce((sum, item) => sum + item, 0);
        setFilterCount(count);
    }, [searchPharmaName, selectedStatuses]);

    const handleStatusChange = (status) => {
        if (selectedStatuses.includes(status)) {
            setSelectedStatuses(selectedStatuses.filter((selected) => selected !== status));
        } else {
            setSelectedStatuses([...selectedStatuses, status]);
        }
        setPagination({ limit: rowsPerPage, offset: 0, page: 1 });
    };

    const handleRowsPerPageChange = (value) => {
        if (value === "Show All") {
            const allRows = totalPharma || 10;
            setRowsPerPage(allRows);
            setPagination({ limit: allRows, offset: 0, page: 1 });
        } else {
            const numericValue = Number(value);
            setRowsPerPage(numericValue);
            setPagination({ limit: numericValue, offset: 0, page: 1 });
        }
    };

    const handleNextPage = () => {
        setPagination({
            limit: rowsPerPage,
            offset: pagination.page * rowsPerPage,
            page: pagination.page + 1,
        });
    };

    const handlePreviousPage = () => {
        setPagination({
            limit: rowsPerPage,
            offset: (pagination.page - 2) * rowsPerPage,
            page: pagination.page - 1,
        });
    };

    const handleClearFilters = () => {
        setSelectedStatuses([]);
        setSearchPharmaName("");
        setSearchVisible(false);
        setPagination({ limit: rowsPerPage, offset: 0, page: 1 });
    };

    const formik = useFormik({
        initialValues: {
            pharma_name: "",
        },
        validationSchema: Yup.object({
            pharma_name: Yup.string().trim().required("Pharma name is required"),
        }),
        onSubmit: async (values, { resetForm, setSubmitting }) => {
            try {
                const payload = editingPharma
                    ? {
                          pharma_id: editingPharma.id,
                          pharma_name: values.pharma_name.trim(),
                      }
                    : {
                          user_id: TokenService.getUserId(),
                          pharma_name: values.pharma_name.trim(),
                      };

                const response = editingPharma
                    ? await RequestorService.editPharma(payload)
                    : await RequestorService.addPharma(payload);

                if (response?.status) {
                    setToast(response.message || "Pharma saved successfully.", "success");
                    resetForm();
                    setEditingPharma(null);
                    setPagination({ limit: rowsPerPage, offset: 0, page: 1 });
                } else {
                    setToast(response?.message || "Unable to save pharma.", "danger");
                }
            } catch (error) {
                console.error("Failed to submit pharma form:", error);
                setToast("Failed to save pharma. Please try again.", "danger");
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleEditPharma = (pharma) => {
        setEditingPharma(pharma);
        formik.setFieldValue("pharma_name", pharma?.pharma_name || "");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const navigate = useNavigate();
    const previousUrl = TokenService.getPrevLink();
    const handleCancelEditing = () => {
        setEditingPharma(null);
        formik.resetForm();
        navigate(`${previousUrl}`);
    };

    const handleUpdatePharmaStatus = async (statusValue) => {
        if (!actionModal.pharma) {
            return;
        }
        let willRefetch = false;
        try {
            setLoading(true);
            const response = await RequestorService.updatePharmaStatus({
                pharma_id: actionModal.pharma.id,
                status: statusValue,
            });

            if (response?.status) {
                setToast(response.message || "Pharma status updated.", "success");
                willRefetch = true;
                fetchPharmaList();
            } else {
                setToast(response?.message || "Unable to update pharma status.", "danger");
            }
        } catch (error) {
            console.error("Failed to update pharma status:", error);
            setToast("Failed to update pharma status. Please try again.", "danger");
        } finally {
            setActionModal({ type: null, pharma: null });
            if (!willRefetch) {
                setLoading(false);
            }
        }
    };

    const openStatusModal = (pharma) => setActionModal({ type: "status", pharma });
    const openDeleteModal = (pharma) => setActionModal({ type: "delete", pharma });
    const closeModal = () => setActionModal({ type: null, pharma: null });

    const isPharmaActive = (pharma) =>  pharma?.pharma_status === "1" || pharma?.status === 1 || pharma?.active === 1 || pharma?.active === true;

    const confirmModalAction = () => {
        if (!actionModal.pharma) {
            return;
        }
        if (actionModal.type === "delete") {
            handleUpdatePharmaStatus(2);
        } else {
            const currentStatus = isPharmaActive(actionModal.pharma) ? 1 : 0;
            const nextStatus = currentStatus === 1 ? 0 : 1;
            handleUpdatePharmaStatus(nextStatus);
        }
    };

    return (
        <>
            <Loader loading={loading} />
            <div className="el-middle-wrapper">
                <Link className="back-link" to={`${previousUrl}`}><i className='el-icon-back'></i> Pharma Management</Link>
                <div className="el-bg-table mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3>{editingPharma ? "Edit Pharma" : "Add Pharma"}</h3>
                        {editingPharma && (
                            <span className="text-muted">Editing: {editingPharma.pharma_name}</span>
                        )}
                    </div>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">
                                        Pharma Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="pharma_name"
                                        className={`form-control ${
                                            formik.touched.pharma_name && formik.errors.pharma_name
                                                ? "is-invalid"
                                                : ""
                                        }`}
                                        placeholder="Enter pharma name"
                                        value={formik.values.pharma_name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.pharma_name && formik.errors.pharma_name && (
                                        <div className="invalid-feedback">{formik.errors.pharma_name}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-3">
                            <button
                                type="submit"
                                className="el-main-button"
                                disabled={formik.isSubmitting}
                            >
                                <span>{editingPharma ? "Update Pharma" : "Save Pharma"}</span>
                            </button>
                            <button
                                type="button"
                                className="back-link"
                                onClick={handleCancelEditing}
                                disabled={formik.isSubmitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>

                <div className="el-bg-table">
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <h3 className="mb-0">Pharma List</h3>
                        <div className="d-flex align-items-center gap-3">
                            {filterCount > 0 && (
                                <span className="fw-semibold">
                                    {filterCount} {filterCount === 1 ? "filter" : "filters"} applied
                                </span>
                            )}
                            {filterCount > 0 && <ClearFilterButton handleClearFilters={handleClearFilters} />}
                        </div>
                    </div>

                    <div className="el-table-container el-board-table">
                        <Table hover>
                            <thead>
                                <tr>
                                    <th>
                                        <SearchInput
                                            label="Pharma Name"
                                            value={searchPharmaName}
                                            setValue={setSearchPharmaName}
                                            isVisible={isSearchVisible}
                                            setVisible={setSearchVisible}
                                        />
                                    </th>
                                    <th>
                                        <AdminStatusDropdown
                                            statuses={pharmaStatuses}
                                            selectedStatuses={selectedStatuses}
                                            handleStatusChange={handleStatusChange}
                                            setSelectedStatuses={setSelectedStatuses}
                                            label="Status"
                                            type="sampleRequest"
                                        />
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pharmaList.length > 0 ? (
                                    pharmaList.map((pharma) => (
                                        <tr key={pharma.id}>
                                            <td>{pharma.pharma_name}</td>
                                            <td>
                                                <div className={isPharmaActive(pharma) ? "el-active" : "el-inactive"}>
                                                    <div
                                                        className={
                                                            isPharmaActive(pharma) ? "el-activebtn" : "el-inactive"
                                                        }
                                                    ></div>
                                                    {isPharmaActive(pharma) ? "Active" : "Inactive"}
                                                </div>
                                            </td>
                                            <td>
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="" className="btn btn-primary dropdown-toggle">
                                                        <img src={navdots} alt="More actions" />
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu className="el-action">
                                                        <Dropdown.Item onClick={() => handleEditPharma(pharma)}>
                                                            <label>Edit</label>
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => openStatusModal(pharma)}>
                                                            <label className={isPharmaActive(pharma) ? "text-danger" : "text-success"}>
                                                                {isPharmaActive(pharma) ? "Deactivate" : "Activate"}
                                                            </label>
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => openDeleteModal(pharma)}>
                                                            <label className="text-danger">Delete</label>
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    ))
                                ) : loading ? (
                                    <tr>
                                        <td colSpan="3" className="text-center py-4">
                                            Loading...
                                        </td>
                                    </tr>
                                ) : (
                                    <NoListDataAvailable type="pharma" />
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <TablePagination
                        rowsPerPage={rowsPerPage || 10}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        pagination={pagination}
                        totalPages={totalPages}
                        handlePreviousPage={handlePreviousPage}
                        handleNextPage={handleNextPage}
                        totalUsers={totalPharma}
                    />
                </div>
            </div>

            <ConfirmationModal
                show={Boolean(actionModal.type)}
                onHide={closeModal}
                onConfirm={confirmModalAction}
                body={
                    actionModal.type === "delete"
                        ? "Are you sure you want to delete this pharma?"
                        : `Are you sure you want to ${actionModal.pharma?.status === 1 ? "deactivate" : "activate"} this pharma?`
                }
                type={actionModal.type === "delete" ? "delete" : "deactivateactivate"}
            />
            <Toaster
                show={toastState.show}
                message={toastState.message}
                variant={toastState.variant}
                onClose={closeToast}
            />
        </>
    );
}

export default AddPharma;